import React, { useContext } from 'react';
import { Card, CardContent, Container, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import axios from 'axios';
import numeral from 'numeral';
import * as Yup from 'yup';
import { useRouter } from "next/router";
import {
  Button,
  ErrorCommon,
  FormikValues,
  FormWrap,
  Label,
  Numeral,
  SelectField,
  TextFieldCommon
} from '../../formikFormInputs';
import EstimationDetail from './EstimationDetail';
import LearnEnrollment from './LearnEnrollment';
import LearnMore from './LearnMore';
import WeWillBeInTouch from './WeWillBeInTouch';
import { SnackbarContext } from '../../Snackbar/Context';
import AmountSlider from '../../Slider';
import { carrierName, stagingServerUrl } from '../../../server/constants';
import { mc, noop } from '../../../utils/commonUtils';
import { fiveYearTrustFee, genderDropDown, initFalse, lowerAgeLimit, upperAgeLimit } from '../../../utils/constants';
import { REQUIRED } from '../../../utils/validation';
import { SnackBarSeverity } from '../../../utils/enums';



const MIN_CONTRIBUTION_AMOUNT = 25000;
const DEFAULT_CONTRIBUTION_AMOUNT = 35000;
const MAX_CONTRIBUTION_AMOUNT = 200000;


const useStyles = makeStyles((theme) =>
  createStyles({
    cardRoot: {
      display: "flex",
      boxShadow: "0 21px 50px 0 rgba(0,0,0,0.28)",
      minHeight: 400,
      position: 'relative',
      marginBottom: 40,
      alignItems: 'center',
      verticalAlign: 'middle',
      [ theme.breakpoints.down("md") ]: {
        minHeight: 260,
      },
      [ theme.breakpoints.down("sm") ]: {
        bottom: '0px !important',
        marginTop: 40,
      },
    },
    details: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    content: {
      flex: "1 0 auto",
      padding: 40,
      [ theme.breakpoints.down("sm") ]: {
        padding: 15,
      },
    },
  }),
);

export default function EstimationFinal(props: any) {
  const classes = useStyles();
  const router = useRouter();
  const snackbar = useContext(SnackbarContext);
  const [estimatedDetail, setEstimatedDetail] = React.useState<any>(undefined);
  const [errorEstimate, setErrorEstimate] = React.useState<any>(undefined);
  const [totalContribution, setTotalContribution] = React.useState<string>("");
  const estimationFieldObj = [
    {
      component: Numeral,
      id: "age",
      label: <Label label={"Your Age"} required={!initFalse}/>,
      name: "age",
      placeholder: "Your Age",
      inputProps: { maxLength: 2 }
    },
    {
      component: SelectField,
      id: "gender",
      label: <Label label={"Your Gender"} required={!initFalse}/>,
      name: "gender",
      onSelectChange: noop,
      options: genderDropDown,
      placeholder: "Your Gender",
    },
  ];
  const initialValues = {age: '', gender: '', contribution: DEFAULT_CONTRIBUTION_AMOUNT};
  const calculateTotalContribution = (contribution: number | string) => {
    let total_5yr_Contribution: number | null = 0;
    if (contribution) {
      const totalContribution: number | null = numeral(contribution).value();
      // @ts-ignore
      total_5yr_Contribution = totalContribution * 5
    }
    if (total_5yr_Contribution > 0) {
      total_5yr_Contribution += fiveYearTrustFee;
    }
    setTotalContribution(total_5yr_Contribution.toFixed(2));
  };
  const calculateEstimateData = async (payload: any) => {
    const getEstimationUrl = `${stagingServerUrl}/estimation/public`;

    await axios.get(getEstimationUrl, {params: payload})
      .then((response) => {
        setEstimatedDetail(response.data.result);
        setErrorEstimate(undefined);
        const erDiv = document.getElementById('graphDiv');
        if (erDiv) {
          erDiv.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'center'});
        }
      })
      .catch((err: any) => {
        const errorEstimate: any = err && err.response && err.response.data && err.response.data.statusCode === 400 && err.response.data.message && err.response.data.message.includes("minimumAmount") && JSON.parse(err.response.data.message);
        setErrorEstimate(errorEstimate);
        if (errorEstimate && errorEstimate.error === "minimumAmount") {
          snackbar.showMessage("Minimum contribution is " + numeral(errorEstimate.value)
            .format("$0,0"), SnackBarSeverity.Error);
          return;
        }

        snackbar.showMessage(errorEstimate, SnackBarSeverity.Error);
      });
  };
  const submitFormik = async (data: FormikValues) => {
    const payload: any = {
      id: router.query.id,
      age: data.age,
      rating: "Standard",
      gender: data.gender,
      contribution: numeral(data.contribution).value(),
      strategySlug: "kaizen",
      carrierName: carrierName,
    };
    calculateEstimateData(payload).then();
    calculateTotalContribution(data.contribution);
  };
  const validateContributionAmount = () => {
    if (errorEstimate && errorEstimate.error === "minimumAmount") {
      return "Minimum contribution is " + numeral(errorEstimate.value).format("$0,0");
    }
    return "";
  };

  const formProps = {
    children: ({
                 dirty,
                 errors,
                 handleBlur,
                 handleChange,
                 handleSubmit,
                 isSubmitting,
                 setFieldValue,
                 values
               }: FormikValues) => {
      return (

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={8}>
              <div className="textCenter first-step textLeftXs">
                <Typography variant="h1" className="mb20 mt80 mt40sm" color="primary">Calculate Your Results</Typography>
                <Typography variant="subtitle1" color="primary">
                  Enter your age, gender and an annual contribution amount to view your estimate.
                </Typography>
                <Grid container className="textCenter mt20" spacing={3} justify="center">
                  {
                    estimationFieldObj.map((field, index) => {
                      return (
                        <Grid item key={index} md={5} xs={12}>
                          <TextFieldCommon
                            {...field}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            values={values}
                          />
                          {
                            field.name === "contribution" &&
                            <div>{validateContributionAmount()}</div>
                          }
                          <ErrorCommon errors={errors} name={field.name} skipTouched={!initFalse}/>
                        </Grid>
                      )
                    })
                  }
                  <Grid item xs={12} md={10}>
                    <AmountSlider
                      className="mt20 mb30 floatLeft"
                      defaultValue={DEFAULT_CONTRIBUTION_AMOUNT}
                      handleChange={(contribution: number) => setFieldValue('contribution', contribution)}
                      mainTitle={<>Annual Contribution Amount <sup className="colorRed">*</sup> </>}
                      max={MAX_CONTRIBUTION_AMOUNT}
                      min={MIN_CONTRIBUTION_AMOUNT}
                      name={"contribution"}
                      sliderClassName={"distributionOuterSpan"}
                    />
                    <Typography variant="caption" className="f12 textLightGray floatLeft"> {mc(MIN_CONTRIBUTION_AMOUNT.toString())} minimum annual requirement for your age</Typography>
                  </Grid>
                  <Grid item xs={12} className="textCenterXs">
                    <Button
                      className="floatRight floatInherit mt20 mb20 customBtn"
                      color="primary"
                      disabled={(isSubmitting || !dirty || Object.keys(errors).length)}
                      endIcon={<ArrowForwardIcon/>}
                      label="View Estimate"
                      type="submit"
                      variant="contained"
                    />
                  </Grid>
                </Grid>
                <div id={"graphDiv"}/>
              </div>
            </Grid>
            <Grid item xs={12}>
              {
                estimatedDetail &&
                <EstimationDetail
                  age={values.age}
                  estimationData={estimatedDetail}
                  totalContribution={totalContribution}
                />
              }
            </Grid>
          </Grid>
        </form>
      )
    },
    initialValues: initialValues,
    onSubmit: submitFormik,
    validationSchema: Yup.object({
      age: Yup.number().required(REQUIRED).min(lowerAgeLimit, `Age must be greater than or equal to ${lowerAgeLimit}`).max(upperAgeLimit, `Age must be less than or equal to ${upperAgeLimit}`).typeError('Age must be a number'),
      gender: Yup.string().required(REQUIRED),
    })
  };

  return (
    <>
      <Container maxWidth="lg">
        <Grid container spacing={2} justifyContent="center" className="pdsM" style={{padding: '160px 15px 0'}}>
          <Grid item xs={12}>
            <FormWrap {...formProps} />
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth="lg">
        <Grid item xs={12}>
          <Card className={classes.cardRoot} style={{bottom: '-232px', zIndex: 1}}>
            <div className={classes.details}>
              <CardContent className={classes.content + " textCenter "}>
                <WeWillBeInTouch/>
              </CardContent>
            </div>
          </Card>
        </Grid>
      </Container>
      <LearnMore/>

      <Container maxWidth="lg">
        <Grid item xs={12}>
          <LearnEnrollment {...props}/>
        </Grid>
      </Container>
    </>
  );
}
